import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Code, 
  Monitor, 
  TestTube, 
  FileText, 
  DollarSign, 
  GitBranch,
  Activity,
  Link,
  Terminal,
  AlertCircle,
  ExternalLink,
  Copy,
  CheckCircle,
  Key,
  Eye,
  EyeOff,
  RefreshCw
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';

export default function DeveloperTools() {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string>(new Date().toLocaleDateString());
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [showApiKey, setShowApiKey] = useState<boolean>(false);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  
  // Check if we're on the user route (API Management only) vs admin route (full developer tools)
  const isApiOnlyMode = window.location.pathname === '/developer/api';

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  // Load API key on component mount
  useEffect(() => {
    loadApiKey();
  }, []);

  const loadApiKey = async () => {
    try {
      const response = await authFetch('/api/users/me/api-key');
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      }
    } catch (error) {
      console.error('Error loading API key:', error);
    }
  };

  const generateApiKey = async () => {
    setIsGenerating(true);
    setApiKeyError(null);
    try {
      const response = await authFetch('/api/users/me/api-key', {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        setApiKey(data.apiKey);
      } else {
        const errorData = await response.json();
        setApiKeyError(errorData.error || 'Failed to generate API key');
      }
    } catch (error) {
      console.error('Error generating API key:', error);
      setApiKeyError('Failed to generate API key');
    } finally {
      setIsGenerating(false);
    }
  };

  // Test Suites Section
  const testSuites = [
    {
      id: 'toy-store-eval',
      name: 'Toy Store Chatbot Evaluation',
      description: 'Comprehensive 100-question evaluation suite with 3 testing modes',
      location: '/tests/toy-store-evaluation',
      commands: [
        { label: 'Interactive Runner', cmd: 'npm run runner:interactive' },
        { label: 'Quick Test (10 questions)', cmd: 'npm run runner:quick' },
        { label: 'Full Evaluation', cmd: 'npm run runner:full' },
        { label: 'Debug Mode (1 question)', cmd: 'npm run runner:debug' },
        { label: 'Browser Mode (Legacy)', cmd: 'npm run evaluate:browser' },
        { label: 'View Content Gaps', cmd: 'npm run view-gaps' }
      ],
      links: [
        { label: 'Evaluation Monitor', url: '/evaluation-monitor.html' },
        { label: 'View Chatbot Logs', url: '/chatbot-logs?siteId=toy-store-local' },
        { label: 'Analytics Dashboard', url: '/chatbot-analytics/toy-store-local' }
      ]
    },
    {
      id: 'agent-tester',
      name: 'Interactive Agent Tester',
      description: 'Web-based interface for testing individual agents in real-time',
      location: '/admin/public/agent-tester.html',
      commands: [],
      links: [
        { label: 'Open Agent Tester', url: '/agent-tester.html' }
      ]
    },
    {
      id: 'api-tests',
      name: 'API Endpoint Tests',
      description: 'Comprehensive API testing suite',
      location: '/admin',
      commands: [
        { label: 'Test All APIs', cmd: 'node test-api-endpoints.js' },
        { label: 'Test Agents', cmd: 'node test-agent-api.js' },
        { label: 'Test Kafka', cmd: 'node test-kafka-integration.js' }
      ],
      links: []
    },
    {
      id: 'chatbot-scenarios',
      name: 'Chatbot Test Scenarios',
      description: 'Automated test runner for chatbot conversation scenarios',
      location: '/agents/page-chatbot-agent/test-scenarios',
      commands: [
        { label: 'Run Tests', cmd: 'node automated-test-runner.js' }
      ],
      links: []
    }
  ];

  // Monitoring Tools Section
  const monitoringTools = [
    {
      name: 'Chatbot Evaluation Monitor',
      url: '/evaluation-monitor.html',
      description: 'Real-time monitoring and analysis of toy store chatbot evaluation results',
      type: 'internal'
    },
    {
      name: 'Enhanced Monitoring Dashboard',
      url: 'http://localhost:3001/monitoring',
      description: 'Real-time WebSocket monitoring with semantic analysis visualization',
      type: 'external'
    },
    {
      name: 'Image Caching Dashboard',
      url: 'http://localhost:4024/dashboard/',
      description: 'Monitor image cache performance, hit ratios, and OpenAI Vision API usage',
      type: 'external'
    },
    {
      name: 'Image Gallery Viewer',
      url: 'http://localhost:4024/gallery/',
      description: 'Visual gallery of cached screenshots with AI analysis results',
      type: 'external'
    },
    {
      name: 'Grafana Metrics',
      url: 'http://localhost:3009',
      description: 'System metrics, performance, and resource usage (admin/P@g3m1nd!)',
      type: 'external'
    },
    {
      name: 'Kafdrop (Kafka UI)',
      url: 'http://localhost:9000',
      description: 'Browse Kafka topics, messages, and consumer groups',
      type: 'external'
    },
    {
      name: 'OpenReplay Sessions',
      url: 'https://replay.pagemind.ai',
      description: 'Visual session recordings and user behavior analysis',
      type: 'external'
    },
    {
      name: 'Matomo Analytics',
      url: '/analytics/matomo',
      description: 'Integrated web analytics and visitor insights',
      type: 'internal'
    },
    {
      name: 'Agent Monitoring',
      url: '/monitoring',
      description: 'PageMind agent status and performance monitoring',
      type: 'internal'
    },
    {
      name: 'Chatbot Debug Monitor',
      url: 'http://localhost:4002/',
      description: 'Real-time chatbot request monitoring and debugging tool',
      type: 'external'
    }
  ];

  // Special Tools Section
  const specialTools = [
    {
      category: 'Database Management',
      tools: [
        { name: 'Manage Agents DB', cmd: 'cd backend-merged && node scripts/manage-agents-db.js', desc: 'Add/remove/update agents in database' },
        { name: 'Check Duplicates', cmd: 'cd backend-merged && node scripts/check-duplicate-agents.js', desc: 'Find duplicate agent entries' },
        { name: 'Verify Agents', cmd: 'cd backend-merged && node scripts/verify-agents.js', desc: 'Verify agent configurations' },
        { name: 'DB Health Check', cmd: 'cd backend-merged && node scripts/check-db.js', desc: 'Check database connectivity and health' }
      ]
    },
    {
      category: 'Analytics Pipeline',
      tools: [
        { name: 'Initialize Analytics', cmd: 'cd backend-merged && node scripts/initialize-chatbot-analytics.js', desc: 'Set up chatbot analytics tables' },
        { name: 'Run ETL Pipeline', cmd: 'cd backend-merged && node scripts/analytics-etl-pipeline.js', desc: 'Process analytics data' },
        { name: 'Test Analytics', cmd: 'cd backend-merged && node scripts/test-chatbot-analytics-system.js', desc: 'Verify analytics system' },
        { name: 'Check Analytics Data', cmd: 'cd backend-merged && node scripts/check-analytics-data.js', desc: 'Validate analytics integrity' }
      ]
    },
    {
      category: 'Kafka Tools',
      tools: [
        { name: 'Test Consumer', cmd: 'cd backend-merged && node scripts/test-kafka-consumer.js', desc: 'Test Kafka consumer connection' },
        { name: 'Test Producer', cmd: 'cd backend-merged && node scripts/test-kafka-producer.js', desc: 'Test Kafka producer' },
        { name: 'Monitor Topics', cmd: 'docker-compose exec kafka kafka-topics --list --bootstrap-server localhost:9092', desc: 'List all Kafka topics' }
      ]
    },
    {
      category: 'Development Utilities',
      tools: [
        { name: 'Git Statistics', cmd: 'open git-statistics-dashboard.html', desc: 'Repository activity visualization' },
        { name: 'OAuth Tester', cmd: 'open /oauth-test.html', desc: 'Test OAuth authentication flow' },
        { name: 'Snowplow Test', cmd: 'open /test-snowplow.html', desc: 'Test Snowplow analytics tracking' }
      ]
    }
  ];

  // Architecture & Documentation
  const architectureDocs = [
    {
      title: 'System Architecture',
      file: 'ARCHITECTURE.md',
      lastUpdate: '2024-01-15',
      keyPoints: [
        'Microservices architecture with 20+ AI agents',
        'Kafka-based event streaming',
        'PostgreSQL with pgvector for embeddings',
        'React admin + Node.js backend',
        'Docker containerization'
      ]
    },
    {
      title: 'Agent Capability Matrix',
      file: 'AGENT-CAPABILITY-MATRIX.md',
      lastUpdate: '2024-01-10',
      keyPoints: [
        'Detailed breakdown of each agent\'s capabilities',
        'Input/output specifications',
        'Dependencies and integration points',
        'Performance characteristics'
      ]
    },
    {
      title: 'Session-Page Architecture',
      file: 'SESSION-PAGE-MANAGER-ARCHITECTURE.md',
      lastUpdate: '2024-01-08',
      keyPoints: [
        'Session lifecycle management',
        'Page context handling',
        'Real-time synchronization',
        'WebSocket communication patterns'
      ]
    }
  ];

  // Cost Assessment
  const costAssessment = {
    lastUpdated: '2024-01-10',
    developmentEffort: {
      total: '18-24 months',
      breakdown: [
        { component: 'Core Platform', effort: '6-8 months', developers: 3 },
        { component: 'AI Agents (20+)', effort: '8-10 months', developers: 4 },
        { component: 'Admin Dashboard', effort: '3-4 months', developers: 2 },
        { component: 'Analytics & Monitoring', effort: '2-3 months', developers: 2 },
        { component: 'Testing & QA', effort: '3-4 months', developers: 2 }
      ]
    },
    teamComposition: {
      total: 8,
      roles: [
        { role: 'Technical Lead', count: 1, cost: '$180k/year' },
        { role: 'Senior Backend Engineers', count: 3, cost: '$150k/year each' },
        { role: 'AI/ML Engineers', count: 2, cost: '$170k/year each' },
        { role: 'Frontend Engineers', count: 2, cost: '$140k/year each' },
        { role: 'DevOps Engineer', count: 1, cost: '$160k/year' },
        { role: 'QA Engineers', count: 2, cost: '$120k/year each' }
      ]
    },
    infrastructure: {
      monthly: '$3,000 - $5,000',
      breakdown: [
        'AWS/Cloud hosting: $1,500-2,500/month',
        'OpenAI API costs: $1,000-2,000/month',
        'Monitoring/Analytics tools: $300-500/month',
        'Other services: $200-300/month'
      ]
    },
    totalEstimate: {
      development: '$2.5M - $3.5M',
      firstYear: '$3.5M - $4.5M (including infrastructure)',
      ongoing: '$1.5M - $2M/year (team + infrastructure)'
    }
  };

  // If in API-only mode, show only API Management
  if (isApiOnlyMode) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">API Management</h1>
          <p className="text-muted-foreground">
            Generate and manage API keys for programmatic access to PageMind data and functionality.
          </p>
        </div>

        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>API Key Management</CardTitle>
              <p className="text-sm text-muted-foreground">
                Generate an API key to access PageMind APIs programmatically. Keep your API key secure and never share it publicly.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="api-key">Your API Key</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Input
                      id="api-key"
                      type={showApiKey ? "text" : "password"}
                      value={apiKey || "No API key generated"}
                      readOnly
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(apiKey || "", "api-key")}
                      disabled={!apiKey}
                    >
                      {copiedId === "api-key" ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  {apiKeyError && (
                    <p className="text-sm text-red-600 mt-1">{apiKeyError}</p>
                  )}
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={generateApiKey}
                    disabled={isGenerating}
                    className="flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4" />
                    )}
                    {apiKey ? "Regenerate API Key" : "Generate API Key"}
                  </Button>
                  {apiKey && (
                    <Button variant="outline" onClick={loadApiKey}>
                      Refresh
                    </Button>
                  )}
                </div>
              </div>

              {apiKey && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Important:</strong> Store your API key securely. If compromised, regenerate it immediately.
                    API keys provide full access to your PageMind data.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Available API Endpoints</CardTitle>
              <p className="text-sm text-muted-foreground">
                Use these endpoints with your API key for programmatic access to PageMind data.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-semibold">Pages API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/v1/pages?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List all pages for a site</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/pages/{`{pageId}`}</code>
                    <p className="text-muted-foreground">Get specific page details</p>
                    <code className="block bg-muted p-2 rounded">PUT /api/v1/pages/{`{pageId}`}</code>
                    <p className="text-muted-foreground">Update page data</p>
                    <code className="block bg-muted p-2 rounded">DELETE /api/v1/pages/{`{pageId}`}</code>
                    <p className="text-muted-foreground">Delete a page</p>
                    <code className="block bg-muted p-2 rounded">POST /api/v1/pages/{`{pageId}`}/enrich</code>
                    <p className="text-muted-foreground">Trigger semantic analysis and image extraction for a page</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Products API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/v1/products?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List all products for a site</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/products/{`{productId}`}</code>
                    <p className="text-muted-foreground">Get specific product details</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Chatbot Logs API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/v1/chatbot-logs?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List chatbot conversations for a site</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/chatbot-logs/{`{sessionId}`}</code>
                    <p className="text-muted-foreground">Get specific conversation details</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Documents API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">POST /api/v1/documents</code>
                    <p className="text-muted-foreground">Upload research documents for enhanced chatbot responses</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/documents?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List documents with optional filtering</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/documents/{`{documentId}`}</code>
                    <p className="text-muted-foreground">Get specific document details</p>
                    <code className="block bg-muted p-2 rounded">DELETE /api/v1/documents/{`{documentId}`}</code>
                    <p className="text-muted-foreground">Delete a document</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Enriched Feed Export API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/sites/{`{siteId}`}/categories/{`{categoryName}`}/export/enriched-feed</code>
                    <p className="text-muted-foreground">Export enriched product feed by category with Q&A metrics</p>
                    <div className="ml-4 text-xs text-muted-foreground">
                      <p><strong>Query params:</strong></p>
                      <ul className="list-disc ml-4">
                        <li>format: 'csv' | 'json' (default: csv)</li>
                        <li>useCanonicalNames: 'true' | 'false' - Use standardized attribute names</li>
                        <li>includeDualFormat: 'true' | 'false' - Include both original + canonical</li>
                        <li>includeEnrichments: 'true' | 'false' - Include Q&A, benefit scores</li>
                        <li>includeProductQuestions: 'true' | 'false' - Include product questions</li>
                      </ul>
                    </div>
                    <code className="block bg-muted p-2 rounded mt-2">POST /api/sites/{`{siteId}`}/question-wheels/{`{wheelId}`}/export-feed</code>
                    <p className="text-muted-foreground">Export enriched feed based on question wheel with answers</p>
                    <div className="ml-4 text-xs text-muted-foreground">
                      <p><strong>Body params:</strong></p>
                      <ul className="list-disc ml-4">
                        <li>format: 'csv' | 'json' (default: csv)</li>
                        <li>includeStatus: array of statuses ['approved', 'pending']</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Authentication</h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">Include your API key in the Authorization header:</p>
                    <code className="block bg-muted p-2 rounded">Authorization: Bearer {`{your-api-key}`}</code>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold mb-2">Example Usage</h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-1">List pages:</p>
                    <code className="text-xs block">
                      curl -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/v1/pages?siteId=SITE_ID"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Upload document:</p>
                    <code className="text-xs block">
                      curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-H "Content-Type: application/json" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-d '{`{"siteId": "SITE_ID", "content": "Document text...", "metadata": {"title": "My Document"}}`}' \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/v1/documents"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Trigger page enrichment:</p>
                    <code className="text-xs block">
                      curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-d '{`{"force": true}`}' \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/v1/pages/PAGE_ID/enrich"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Export enriched feed by category:</p>
                    <code className="text-xs block">
                      curl -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/sites/SITE_ID/categories/CATEGORY_NAME/export/enriched-feed?format=json"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Export enriched feed by question wheel:</p>
                    <code className="text-xs block">
                      curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-H "Content-Type: application/json" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-d '{`{"format": "csv", "includeStatus": ["approved", "pending"]}`}' \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/sites/SITE_ID/question-wheels/WHEEL_ID/export-feed"
                    </code>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Full developer tools for admin route
  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Developer Tools & Documentation</h1>
        <p className="text-muted-foreground">
          Comprehensive collection of all testing tools, monitoring dashboards, and development resources built for PageMind
        </p>
        <Badge variant="outline" className="mt-2">
          Last Updated: {lastUpdated}
        </Badge>
      </div>

      <Tabs defaultValue="test-suites" className="space-y-4">
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="test-suites" className="flex items-center gap-2">
            <TestTube className="w-4 h-4" />
            Test Suites
          </TabsTrigger>
          <TabsTrigger value="monitoring" className="flex items-center gap-2">
            <Monitor className="w-4 h-4" />
            Monitoring
          </TabsTrigger>
          <TabsTrigger value="api-management" className="flex items-center gap-2">
            <Key className="w-4 h-4" />
            API Management
          </TabsTrigger>
          <TabsTrigger value="tools" className="flex items-center gap-2">
            <Terminal className="w-4 h-4" />
            Special Tools
          </TabsTrigger>
          <TabsTrigger value="architecture" className="flex items-center gap-2">
            <GitBranch className="w-4 h-4" />
            Architecture
          </TabsTrigger>
          <TabsTrigger value="cost" className="flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Cost Assessment
          </TabsTrigger>
        </TabsList>

        <TabsContent value="test-suites" className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              These test suites help validate chatbot functionality, API endpoints, and agent behavior. 
              Always run tests after making changes to ensure system stability.
            </AlertDescription>
          </Alert>

          {testSuites.map((suite) => (
            <Card key={suite.id}>
              <CardHeader>
                <CardTitle className="text-lg">{suite.name}</CardTitle>
                <p className="text-sm text-muted-foreground">{suite.description}</p>
                <code className="text-xs bg-muted px-2 py-1 rounded">{suite.location}</code>
              </CardHeader>
              <CardContent className="space-y-4">
                {suite.commands.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Commands:</h4>
                    <div className="space-y-2">
                      {suite.commands.map((cmd, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-muted/50 p-2 rounded">
                          <div>
                            <span className="text-sm font-medium">{cmd.label}:</span>
                            <code className="ml-2 text-sm font-mono">{cmd.cmd}</code>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(cmd.cmd, `${suite.id}-cmd-${idx}`)}
                          >
                            {copiedId === `${suite.id}-cmd-${idx}` ? (
                              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                            ) : (
                              <Copy className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {suite.links.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Related Links:</h4>
                    <div className="flex flex-wrap gap-2">
                      {suite.links.map((link, idx) => (
                        <Button
                          key={idx}
                          size="sm"
                          variant="outline"
                          onClick={() => window.open(link.url, '_blank')}
                        >
                          {link.label}
                          <ExternalLink className="w-3 h-3 ml-1" />
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="monitoring" className="space-y-4">
          <Alert>
            <Activity className="h-4 w-4" />
            <AlertDescription>
              Real-time monitoring tools to track system performance, agent behavior, and user interactions.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {monitoringTools.map((tool, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => window.open(tool.url, tool.type === 'external' ? '_blank' : '_self')}>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center justify-between">
                    {tool.name}
                    <Badge variant={tool.type === 'external' ? 'secondary' : 'default'}>
                      {tool.type}
                    </Badge>
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">{tool.description}</p>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center text-sm text-primary">
                    <Link className="w-4 h-4 mr-1" />
                    {tool.url}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="api-management" className="space-y-4">
          <Alert>
            <Key className="h-4 w-4" />
            <AlertDescription>
              Generate and manage API keys for programmatic access to PageMind data and functionality.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>API Key Management</CardTitle>
              <p className="text-sm text-muted-foreground">
                Generate an API key to access PageMind APIs programmatically. Keep your API key secure and never share it publicly.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="api-key">Your API Key</Label>
                  <div className="flex items-center space-x-2 mt-1">
                    <Input
                      id="api-key"
                      type={showApiKey ? "text" : "password"}
                      value={apiKey || "No API key generated"}
                      readOnly
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowApiKey(!showApiKey)}
                    >
                      {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(apiKey || "", "api-key")}
                      disabled={!apiKey}
                    >
                      {copiedId === "api-key" ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  {apiKeyError && (
                    <p className="text-sm text-red-600 mt-1">{apiKeyError}</p>
                  )}
                </div>

                <div className="flex space-x-2">
                  <Button
                    onClick={generateApiKey}
                    disabled={isGenerating}
                    className="flex items-center gap-2"
                  >
                    {isGenerating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Key className="w-4 h-4" />
                    )}
                    {apiKey ? "Regenerate API Key" : "Generate API Key"}
                  </Button>
                  {apiKey && (
                    <Button variant="outline" onClick={loadApiKey}>
                      Refresh
                    </Button>
                  )}
                </div>
              </div>

              {apiKey && (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Important:</strong> Store your API key securely. If compromised, regenerate it immediately.
                    API keys provide full access to your PageMind data.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Available API Endpoints</CardTitle>
              <p className="text-sm text-muted-foreground">
                Use these endpoints with your API key for programmatic access to PageMind data.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <h4 className="font-semibold">Pages API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/v1/pages?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List all pages for a site</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/pages/{`{pageId}`}</code>
                    <p className="text-muted-foreground">Get specific page details</p>
                    <code className="block bg-muted p-2 rounded">PUT /api/v1/pages/{`{pageId}`}</code>
                    <p className="text-muted-foreground">Update page data</p>
                    <code className="block bg-muted p-2 rounded">DELETE /api/v1/pages/{`{pageId}`}</code>
                    <p className="text-muted-foreground">Delete a page</p>
                    <code className="block bg-muted p-2 rounded">POST /api/v1/pages/{`{pageId}`}/enrich</code>
                    <p className="text-muted-foreground">Trigger semantic analysis and image extraction for a page</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Products API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/v1/products?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List all products for a site</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/products/{`{productId}`}</code>
                    <p className="text-muted-foreground">Get specific product details</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Chatbot Logs API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/v1/chatbot-logs?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List chatbot conversations for a site</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/chatbot-logs/{`{sessionId}`}</code>
                    <p className="text-muted-foreground">Get specific conversation details</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Documents API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">POST /api/v1/documents</code>
                    <p className="text-muted-foreground">Upload research documents for enhanced chatbot responses</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/documents?siteId={`{siteId}`}</code>
                    <p className="text-muted-foreground">List documents with optional filtering</p>
                    <code className="block bg-muted p-2 rounded">GET /api/v1/documents/{`{documentId}`}</code>
                    <p className="text-muted-foreground">Get specific document details</p>
                    <code className="block bg-muted p-2 rounded">DELETE /api/v1/documents/{`{documentId}`}</code>
                    <p className="text-muted-foreground">Delete a document</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Enriched Feed Export API</h4>
                  <div className="space-y-1 text-sm">
                    <code className="block bg-muted p-2 rounded">GET /api/sites/{`{siteId}`}/categories/{`{categoryName}`}/export/enriched-feed</code>
                    <p className="text-muted-foreground">Export enriched product feed by category with Q&A metrics</p>
                    <div className="ml-4 text-xs text-muted-foreground">
                      <p><strong>Query params:</strong></p>
                      <ul className="list-disc ml-4">
                        <li>format: 'csv' | 'json' (default: csv)</li>
                        <li>useCanonicalNames: 'true' | 'false' - Use standardized attribute names</li>
                        <li>includeDualFormat: 'true' | 'false' - Include both original + canonical</li>
                        <li>includeEnrichments: 'true' | 'false' - Include Q&A, benefit scores</li>
                        <li>includeProductQuestions: 'true' | 'false' - Include product questions</li>
                      </ul>
                    </div>
                    <code className="block bg-muted p-2 rounded mt-2">POST /api/sites/{`{siteId}`}/question-wheels/{`{wheelId}`}/export-feed</code>
                    <p className="text-muted-foreground">Export enriched feed based on question wheel with answers</p>
                    <div className="ml-4 text-xs text-muted-foreground">
                      <p><strong>Body params:</strong></p>
                      <ul className="list-disc ml-4">
                        <li>format: 'csv' | 'json' (default: csv)</li>
                        <li>includeStatus: array of statuses ['approved', 'pending']</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold">Authentication</h4>
                  <div className="space-y-1 text-sm">
                    <p className="text-muted-foreground">Include your API key in the Authorization header:</p>
                    <code className="block bg-muted p-2 rounded">Authorization: Bearer {`{your-api-key}`}</code>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <h4 className="font-semibold mb-2">Example Usage</h4>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium mb-1">List pages:</p>
                    <code className="text-xs block">
                      curl -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/v1/pages?siteId=SITE_ID"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Upload document:</p>
                    <code className="text-xs block">
                      curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-H "Content-Type: application/json" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-d '{`{"siteId": "SITE_ID", "content": "Document text...", "metadata": {"title": "My Document"}}`}' \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/v1/documents"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Trigger page enrichment:</p>
                    <code className="text-xs block">
                      curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-d '{`{"force": true}`}' \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/v1/pages/PAGE_ID/enrich"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Export enriched feed by category:</p>
                    <code className="text-xs block">
                      curl -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/sites/SITE_ID/categories/CATEGORY_NAME/export/enriched-feed?format=json"
                    </code>
                  </div>
                  <div>
                    <p className="text-sm font-medium mb-1">Export enriched feed by question wheel:</p>
                    <code className="text-xs block">
                      curl -X POST -H "Authorization: Bearer YOUR_API_KEY" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-H "Content-Type: application/json" \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;-d '{`{"format": "csv", "includeStatus": ["approved", "pending"]}`}' \<br />
                      &nbsp;&nbsp;&nbsp;&nbsp;"http://localhost:3001/api/sites/SITE_ID/question-wheels/WHEEL_ID/export-feed"
                    </code>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tools" className="space-y-4">
          <Alert>
            <Code className="h-4 w-4" />
            <AlertDescription>
              Specialized scripts and utilities for database management, analytics processing, and system debugging.
            </AlertDescription>
          </Alert>

          {specialTools.map((category) => (
            <Card key={category.category}>
              <CardHeader>
                <CardTitle className="text-lg">{category.category}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {category.tools.map((tool, idx) => (
                    <div key={idx} className="border-b last:border-0 pb-2 last:pb-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium">{tool.name}</h4>
                          <p className="text-sm text-muted-foreground">{tool.desc}</p>
                          <code className="text-xs bg-muted px-2 py-1 rounded inline-block mt-1">
                            {tool.cmd}
                          </code>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(tool.cmd, `tool-${category.category}-${idx}`)}
                        >
                          {copiedId === `tool-${category.category}-${idx}` ? (
                            <CheckCircle className="w-4 h-4 text-green-600" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="architecture" className="space-y-4">
          <Alert>
            <FileText className="h-4 w-4" />
            <AlertDescription>
              Key architectural documents and system design specifications.
            </AlertDescription>
          </Alert>

          {architectureDocs.map((doc, idx) => (
            <Card key={idx}>
              <CardHeader>
                <CardTitle className="text-lg flex items-center justify-between">
                  {doc.title}
                  <Badge variant="outline">Updated: {doc.lastUpdate}</Badge>
                </CardTitle>
                <code className="text-sm text-muted-foreground">{doc.file}</code>
              </CardHeader>
              <CardContent>
                <h4 className="font-semibold mb-2">Key Points:</h4>
                <ul className="list-disc list-inside space-y-1">
                  {doc.keyPoints.map((point, pidx) => (
                    <li key={pidx} className="text-sm">{point}</li>
                  ))}
                </ul>
                <Button
                  className="mt-4"
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`/${doc.file}`, '_blank')}
                >
                  View Document
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="cost" className="space-y-4">
          <Alert>
            <DollarSign className="h-4 w-4" />
            <AlertDescription>
              Estimated costs for professional development of PageMind by a real development team.
              Last assessment: {costAssessment.lastUpdated}
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Development Effort Breakdown</CardTitle>
              <p className="text-sm text-muted-foreground">
                Total estimated timeline: {costAssessment.developmentEffort.total}
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {costAssessment.developmentEffort.breakdown.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <div>
                      <h4 className="font-medium">{item.component}</h4>
                      <p className="text-sm text-muted-foreground">{item.developers} developers</p>
                    </div>
                    <Badge>{item.effort}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Team Composition & Costs</CardTitle>
              <p className="text-sm text-muted-foreground">
                Total team size: {costAssessment.teamComposition.total} people
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {costAssessment.teamComposition.roles.map((role, idx) => (
                  <div key={idx} className="flex justify-between items-center p-3 bg-muted/50 rounded">
                    <div>
                      <h4 className="font-medium">{role.role}</h4>
                      <p className="text-sm text-muted-foreground">Count: {role.count}</p>
                    </div>
                    <Badge variant="secondary">{role.cost}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Infrastructure Costs</CardTitle>
              <p className="text-sm text-muted-foreground">
                Monthly estimate: {costAssessment.infrastructure.monthly}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="list-disc list-inside space-y-1">
                {costAssessment.infrastructure.breakdown.map((item, idx) => (
                  <li key={idx} className="text-sm">{item}</li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card className="border-2 border-primary/20 bg-primary/5">
            <CardHeader>
              <CardTitle className="text-xl">Total Cost Estimate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">Development (one-time):</span>
                  <span className="font-bold">{costAssessment.totalEstimate.development}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">First Year Total:</span>
                  <span className="font-bold">{costAssessment.totalEstimate.firstYear}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">Ongoing Annual:</span>
                  <span className="font-bold">{costAssessment.totalEstimate.ongoing}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="mt-8 p-4 bg-muted rounded-lg">
        <h3 className="font-semibold mb-2">Quick Reference</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <h4 className="font-medium">Docker Commands</h4>
            <code className="text-xs">docker-compose ps</code><br />
            <code className="text-xs">docker-compose logs -f [service]</code>
          </div>
          <div>
            <h4 className="font-medium">Common Ports</h4>
            <code className="text-xs">:3000 - Admin UI</code><br />
            <code className="text-xs">:3001 - Backend API</code><br />
            <code className="text-xs">:9000 - Kafdrop</code>
          </div>
          <div>
            <h4 className="font-medium">Test Databases</h4>
            <code className="text-xs">pagemind (main)</code><br />
            <code className="text-xs">postgres/pagemind</code>
          </div>
          <div>
            <h4 className="font-medium">Key Files</h4>
            <code className="text-xs">CLAUDE.md</code><br />
            <code className="text-xs">.env</code><br />
            <code className="text-xs">docker-compose.yml</code>
          </div>
        </div>
      </div>
    </div>
  );
}